import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import useResizer, { SLIDE_SELECTION_CLEAR } from '../../hooks/use-resizer';
import { slideAction, saveMirrorToCloudinary } from '../../store/slideSlice';
import { slideAPI } from '../../store/api';
import { emitWordArtTextUpdate } from '../../Socket/Socket';
import {
  deltaToPlainText,
  plainTextToDelta,
  buildWordArtStyles,
  buildWordArtDisplayStyles,
  getWordArtEditStyles,
  getWordArtScale,
  hasReflection,
  isSameWordArtStyles,
  consumeWordArtEditRequest,
  WORD_ART_REF_WIDTH,
  WORD_ART_REF_HEIGHT,
} from './wordArtUtils';
import { updateTextboxDelta } from '../../utils/helper';
import { getSnapshot } from '../../utils/utils';
import { useObjectAnimation } from '../Animations/hooks/AnimationContext';
import classes from './WordArt.module.css';

export const WordArtTextObject = ({
  wordArtId,
  currentSlideIdProp = null,
  isPrint = false,
  printData = null,
}) => {
  const { id: presentationId } = useParams();
  const dispatch = useDispatch();
  const wordArtAnim = useObjectAnimation(wordArtId);
  const [addResizer, removeResizer, dragEvent] = useResizer();
  const [isSelected, setIsSelected] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isDraggingRef = useRef(false);
  const [scaledEffectStyles, setScaledEffectStyles] = useState({});
  const [shadowWrapperStyles, setShadowWrapperStyles] = useState({});
  const [scaleTransform, setScaleTransform] = useState({ scaleX: 1, scaleY: 1 });
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const scaleRafRef = useRef(null);
  const isEditingRef = useRef(false);
  // A freshly inserted WordArt selects its placeholder so the first keystroke
  // replaces it; a double-click into an existing one puts the caret at the end.
  const selectAllOnEditRef = useRef(false);

  let currentSlideId = useSelector((state) => state.slide.currentSlideId);
  if (currentSlideIdProp) currentSlideId = currentSlideIdProp;

  const storeData = useSelector(
    (state) => state.slide.slides[currentSlideId]?.wordArtTextObjects?.[wordArtId]
  );
  const wordArtData = printData || storeData;
  const isReadOnlyElement = wordArtData?.styles?.pointerEvents === 'none';

  const presentMode = useSelector((state) => state.slide.presentMode);
  const isMasterSlide = useSelector((state) => state.slide.isMasterSlide);
  const masterSlideId = useSelector((state) => state.slide.masterSlideId);
  const existingSlides = useSelector((state) => state.slide.slides);
  const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);

  const persistTextEdit = useRef(
    debounce((textDelta, slideId, masterMode, masterId, slides, mirrors) => {
      if (!masterMode) {
        const payload = { currentSlideId: slideId, wordArtId, content: textDelta };
        const updatedSlides = updateTextboxDelta(slides, payload, 'wordart');
        slideAPI.updateWordArtTextInDatabase(
          presentationId,
          slideId,
          wordArtId,
          textDelta,
          dispatch,
          { slides: updatedSlides, mirrorSlides: mirrors }
        );
        emitWordArtTextUpdate({
          roomId: presentationId,
          elementData: { slideId, wordArtId, content: textDelta },
        });
      } else {
        slideAPI.updateWordArtTextInDatabase(
          presentationId,
          masterId,
          wordArtId,
          textDelta
        );
      }
    }, 500)
  ).current;

  const commitTextEdit = useCallback(
    (rawText) => {
      const text = (rawText ?? '').trim();
      const textDelta = plainTextToDelta(text);
      dispatch(
        slideAction.updateWordArtText({
          slideId: currentSlideId,
          wordArtId,
          content: textDelta,
        })
      );
      persistTextEdit(
        textDelta,
        currentSlideId,
        isMasterSlide,
        masterSlideId,
        existingSlides,
        mirrorSlides
      );
      getSnapshot().then((dataUrl) => {
        dispatch(
          saveMirrorToCloudinary({
            dataId: currentSlideId,
            mirrorSlideDataUrl: dataUrl,
            presentationId,
          })
        );
      });
    },
    [
      currentSlideId,
      isMasterSlide,
      masterSlideId,
      existingSlides,
      mirrorSlides,
      presentationId,
      dispatch,
      persistTextEdit,
      wordArtId,
    ]
  );

  const exitEditing = useCallback(
    (shouldSave = true) => {
      if (!isEditingRef.current) return;
      if (shouldSave && textRef.current) {
        commitTextEdit(textRef.current.innerText);
      }
      isEditingRef.current = false;
      setIsEditing(false);
    },
    [commitTextEdit]
  );

  const handleMouseDown = (e) => {
    if (presentMode || isPrint || isEditing || isReadOnlyElement || e.button !== 0) return;
    if (e.target.classList?.contains('resizer')) return;
    e.stopPropagation();
    setIsSelected(true);
    isDraggingRef.current = true;
    dragEvent(e);
  };

  const handleClick = (e) => {
    if (presentMode || isPrint || isEditing || isReadOnlyElement) return;
    if (e.target.classList?.contains('resizer')) return;
    e.stopPropagation();
    setIsSelected(true);
  };

  const startEditing = (e) => {
    if (presentMode || isPrint || isEditing || isReadOnlyElement) return;
    e.stopPropagation();
    e.preventDefault();
    setIsSelected(true);
    isEditingRef.current = true;
    setIsEditing(true);
  };

  const handleBlur = () => {
    if (isDraggingRef.current) return;
    setIsSelected(false);
  };

  const handleTextBlur = () => {
    exitEditing(true);
  };

  const handleTextKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      exitEditing(true);
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || presentMode || isPrint || isReadOnlyElement) return;
    if (isSelected) {
      addResizer(el);
    } else {
      removeResizer(el);
    }
    return () => removeResizer(el);
  }, [isSelected, presentMode, isPrint, isReadOnlyElement, addResizer, removeResizer]);

  useEffect(() => {
    const onDocumentMouseUp = () => {
      if (!isDraggingRef.current) return;
      requestAnimationFrame(() => {
        isDraggingRef.current = false;
        const el = containerRef.current;
        if (el && isSelected) {
          addResizer(el);
        }
      });
    };
    document.addEventListener('mouseup', onDocumentMouseUp);
    return () => document.removeEventListener('mouseup', onDocumentMouseUp);
  }, [isSelected, addResizer]);

  useEffect(() => {
    if (!isEditing) return;
    const frame = requestAnimationFrame(() => {
      const el = textRef.current;
      if (!el) return;
      el.textContent = deltaToPlainText(wordArtData?.delta);
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      // Leaving the range uncollapsed selects the whole placeholder, so typing
      // replaces it (PowerPoint's behaviour on insert).
      if (selectAllOnEditRef.current) {
        selectAllOnEditRef.current = false;
      } else {
        range.collapse(false);
      }
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    });
    return () => cancelAnimationFrame(frame);
  }, [isEditing, wordArtData?.delta]);

  // A WordArt just inserted by this user opens straight into edit mode. The
  // request is consumed on mount, so it never fires for objects that arrive via
  // the socket or a slide reload.
  useEffect(() => {
    if (presentMode || isPrint || isReadOnlyElement) return;
    if (!consumeWordArtEditRequest(wordArtId)) return;
    setIsSelected(true);
    isEditingRef.current = true;
    selectAllOnEditRef.current = true;
    setIsEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordArtId]);

  useEffect(() => {
    const onSelectionClear = (e) => {
      if (e.detail?.exceptId === wordArtId) return;
      setIsSelected(false);
    };
    window.addEventListener(SLIDE_SELECTION_CLEAR, onSelectionClear);
    return () => window.removeEventListener(SLIDE_SELECTION_CLEAR, onSelectionClear);
  }, [wordArtId]);

  useEffect(() => {
    const handlePointerDownOutside = (e) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target)) return;
      if (e.target.classList?.contains('resizer')) return;
      if (isEditingRef.current) {
        exitEditing(true);
      }
      setIsSelected(false);
    };
    document.addEventListener('mousedown', handlePointerDownOutside, true);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside, true);
  }, [exitEditing]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !wordArtData?.effects) return;

    const updateScaledStyles = () => {
      if (scaleRafRef.current) return;
      scaleRafRef.current = requestAnimationFrame(() => {
        scaleRafRef.current = null;
        // offsetWidth/offsetHeight give the layout box in this element's own CSS
        // pixels. getBoundingClientRect() reports viewport pixels instead, so in
        // present mode - where the slide carries `zoom: 160%` - it came back 1.6x
        // too large. That inflated width became an extra transform: scale() on
        // top of the zoom, and the WordArt was drawn far bigger than its box.
        // (SmartArtObject measures the same way, for the same reason.)
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        const { scaleX, scaleY } = getWordArtScale(width, height);
        setScaleTransform((prev) =>
          prev.scaleX === scaleX && prev.scaleY === scaleY ? prev : { scaleX, scaleY }
        );

        const text = isEditingRef.current && textRef.current
          ? textRef.current.innerText
          : deltaToPlainText(wordArtData.delta);
        const { textStyles, wrapperStyle } = buildWordArtDisplayStyles(
          wordArtData.effects,
          WORD_ART_REF_WIDTH,
          WORD_ART_REF_HEIGHT,
          text
        );
        setScaledEffectStyles((prev) =>
          isSameWordArtStyles(prev, textStyles) ? prev : textStyles
        );
        setShadowWrapperStyles((prev) =>
          prev.filter === wrapperStyle.filter ? prev : wrapperStyle
        );
      });
    };

    updateScaledStyles();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateScaledStyles);
      observer.observe(el);
      return () => {
        observer.disconnect();
        if (scaleRafRef.current) {
          cancelAnimationFrame(scaleRafRef.current);
          scaleRafRef.current = null;
        }
      };
    }

    return () => {
      if (scaleRafRef.current) {
        cancelAnimationFrame(scaleRafRef.current);
        scaleRafRef.current = null;
      }
    };
  }, [wordArtData?.effects, wordArtData?.delta, isEditing]);

  if (!wordArtData) return null;

  const plainText = deltaToPlainText(wordArtData.delta);
  const isEmpty = !plainText.trim();
  const displayStyles = Object.keys(scaledEffectStyles).length
    ? scaledEffectStyles
    : buildWordArtStyles(wordArtData.effects);
  const editStyles = getWordArtEditStyles(displayStyles);
  const showReflection = hasReflection(wordArtData.effects) && !isEditing;

  const reflectionVars = showReflection
    ? {
        '--reflection-opacity': wordArtData.effects.reflection.opacity ?? 0.35,
        '--reflection-distance': wordArtData.effects.reflection.distance ?? '4px',
        '--reflection-blur': wordArtData.effects.reflection.blur ?? '1px',
      }
    : {};

  const textEl = (
    <div className={classes.wordArtTextWrap} style={shadowWrapperStyles}>
      <div
        ref={textRef}
        className={`${classes.wordArtText} ${isEditing ? classes.wordArtTextEditing : classes.wordArtTextDisplay} ${!isEditing && isEmpty ? classes.wordArtTextEmpty : ''}`}
        style={isEditing ? editStyles : displayStyles}
        contentEditable={isEditing}
        suppressContentEditableWarning
        data-placeholder={isEditing ? undefined : 'Double-click to edit'}
        onDoubleClick={!isEditing ? startEditing : undefined}
        onBlur={isEditing ? handleTextBlur : undefined}
        onKeyDown={isEditing ? handleTextKeyDown : undefined}
      >
        {!isEditing ? plainText : null}
      </div>
    </div>
  );

  const scaledContent = showReflection ? (
    <div className={classes.wordArtReflectionWrap} style={reflectionVars}>
      {textEl}
      <div className={classes.wordArtReflectionWrap} style={shadowWrapperStyles} aria-hidden="true">
        <div className={classes.wordArtReflection} style={displayStyles}>
          {plainText}
        </div>
      </div>
    </div>
  ) : (
    textEl
  );

  return (
    <div
      ref={containerRef}
      id={wordArtId}
      tabIndex={0}
      className={`${classes.wordArtContainer} wordart`}
      style={{ ...wordArtData.styles, ...wordArtAnim.style }}
      onMouseDown={presentMode || isPrint ? undefined : handleMouseDown}
      onClick={presentMode || isPrint ? undefined : handleClick}
      onBlur={presentMode || isPrint ? undefined : handleBlur}
    >
      <div
        className={classes.wordArtScaleWrap}
        style={{
          transform: `scale(${scaleTransform.scaleX}, ${scaleTransform.scaleY})`,
        }}
      >
        {scaledContent}
      </div>
    </div>
  );
};

export default WordArtTextObject;
